import React from 'react';
import { useTranslate, usePermissions, useGetList } from 'react-admin';
import { Alert, AlertTitle, Typography } from '@mui/material';
import { hasPermission } from '../../utils/hasPermission';

const CHECKED_PER_PAGE = 200;
const NAMED_LIMIT = 5;

interface SiteKioskBayNumberWarningProps {
    siteId: string;
}

const hasReferenceNumber = (value: unknown): boolean => typeof value === 'string' && value.trim().length > 0;

export const SiteKioskBayNumberWarning: React.FC<SiteKioskBayNumberWarningProps> = ({ siteId }) => {
    const translate = useTranslate();
    const { permissions, isLoading: permissionsLoading } = usePermissions();
    const canReadChargers =
        !permissionsLoading && hasPermission(permissions as string[] | null | undefined, 'chargers:read:all');

    const { data: chargers, total } = useGetList(
        'chargers',
        {
            filter: { siteId },
            pagination: { page: 1, perPage: CHECKED_PER_PAGE },
            sort: { field: 'name', order: 'ASC' },
        },
        { enabled: canReadChargers && Boolean(siteId) }
    );

    if (!canReadChargers || !chargers) return null;

    const missing = chargers.filter((charger) => !hasReferenceNumber((charger as any)?.bayNumber));
    if (missing.length === 0) return null;

    const named = missing.slice(0, NAMED_LIMIT).map((charger) => String(charger.name));
    const rest = missing.length - named.length;
    const names = rest > 0 ? [...named, translate('resources.sites.kioskBayWarning.andMore', { count: rest })] : named;
    const checkedEverything = typeof total !== 'number' || total <= chargers.length;

    return (
        <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>{translate('resources.sites.kioskBayWarning.title')}</AlertTitle>
            <Typography variant="body2">
                {translate('resources.sites.kioskBayWarning.body', { names: names.join(', ') })}
            </Typography>
            {!checkedEverything && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                    {translate('resources.sites.kioskBayWarning.partial', { count: chargers.length })}
                </Typography>
            )}
        </Alert>
    );
};
